import type { KeywordFilter } from "../../Topics/filter";

import { parseFilterTerm } from "./parseFilterTerm";

export interface MatchResult {
  matches: boolean;
  /**
   * Position of the first non-negated token's first match in the text. Undefined
   * if the matcher has no positive tokens, the result was inverted, or no
   * match position is determinable.
   */
  position?: [number, number];
}

export type Matcher = (text: string) => MatchResult;

interface CompiledLiteralToken {
  kind: "literal";
  loweredPattern: string;
  rawPattern: string;
  negate: boolean;
}

interface CompiledRegexToken {
  kind: "regex";
  regex: RegExp;
  negate: boolean;
}

type CompiledToken = CompiledLiteralToken | CompiledRegexToken;

const MATCHES_ALL: Matcher = () => ({ matches: true });

/**
 * Builds a `Matcher` for the given keyword filter.
 *
 * - Empty term → matches everything, no position.
 * - Literal mode → AND across all tokens (negated tokens excluded). Position
 *   comes from the first positive token's `indexOf`.
 * - Regex mode → single pattern with optional `!` invert.
 * - Invalid regex pattern → matches everything (the FilterInput surfaces the
 *   syntax error visually; we don't want a typo while drafting a regex to
 *   silently empty the entire list).
 */
export function makeMatcher(filter: KeywordFilter): Matcher {
  const parsed = parseFilterTerm(filter.term, filter.isRegex);

  if (parsed.tokens.length === 0) {
    return MATCHES_ALL;
  }

  const compiled: CompiledToken[] = [];
  for (const token of parsed.tokens) {
    if (filter.isRegex) {
      const regex = compileRegex(token.pattern);
      if (regex === null) {
        // See the JSDoc above: invalid regex falls through to "no filter"
        // rather than "match nothing" so the user sees their list, not an
        // empty void, while drafting a pattern.
        return MATCHES_ALL;
      }
      compiled.push({ kind: "regex", regex, negate: token.negate });
    } else {
      compiled.push({
        kind: "literal",
        loweredPattern: token.pattern.toLowerCase(),
        rawPattern: token.pattern,
        negate: token.negate,
      });
    }
  }

  // First non-negated token (if any) is the source of position information.
  const firstPositive = compiled.find((t) => !t.negate);

  // Are there any literal tokens? If so, we'll lower the text once per call
  // instead of once per (literal-)token-per-call.
  const hasLiteralToken = compiled.some((t) => t.kind === "literal");

  return function match(text: string): MatchResult {
    const loweredText = hasLiteralToken ? text.toLowerCase() : "";

    let allTokensSatisfied = true;
    for (const token of compiled) {
      const hit = testToken(token, text, loweredText);
      const required = !token.negate;
      if (hit !== required) {
        allTokensSatisfied = false;
        break;
      }
    }

    if (parsed.isWholePatternInvert) {
      // Whole-pattern invert applies to the combined result. No position is
      // meaningful when we matched by NOT matching.
      return { matches: !allTokensSatisfied };
    }

    if (!allTokensSatisfied) {
      return { matches: false };
    }

    if (firstPositive === undefined) {
      // Only negated tokens; no position to highlight.
      return { matches: true };
    }

    const position = findPosition(firstPositive, text, loweredText);
    return position ? { matches: true, position } : { matches: true };
  };
}

function compileRegex(pattern: string): RegExp | null {
  try {
    return new RegExp(pattern, "i");
  } catch {
    return null;
  }
}

function testToken(
  token: CompiledToken,
  text: string,
  loweredText: string,
): boolean {
  if (token.kind === "literal") {
    return loweredText.includes(token.loweredPattern);
  }
  return token.regex.test(text);
}

function findPosition(
  token: CompiledToken,
  text: string,
  loweredText: string,
): [number, number] | undefined {
  if (token.kind === "literal") {
    const idx = loweredText.indexOf(token.loweredPattern);
    if (idx === -1) {
      return undefined;
    }
    return [idx, idx + token.rawPattern.length];
  }

  // Regex tokens are compiled without the `g` flag, so `exec` always restarts
  // at index 0 — safe to call across many texts without state leakage.
  const m = token.regex.exec(text);
  if (m === null) {
    return undefined;
  }
  return [m.index, m.index + m[0].length];
}
